import { z } from 'zod';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { AnyRecord } from 'utility/types/cosmosTypes';
import { CreateZodValidatorFnParams } from 'utility/zodFunctions';
import { removeAllEmptyNullUndefinedValues } from 'utility/functions';
import {
  authTypeChoices,
  bannedChoices,
  subscriptionStateChoices,
  userStatusChoices,
  userSubViewByChoices,
} from 'components/Inputs/Dropdown/dropdownConsts';
import {
  editorialSortOptions,
  userSortOptions,
  servicesSortOptions,
  transactionsSortOptions,
  broadcastMessageSortOptions,
  broadcastChannelSortOptions,
  dashboardSortOptions,
  channelsSortOptions,
} from './const';
import { ValidListFilterType, SearchContextType } from './types';
import { TFunction } from 'i18next';
import { SaveSearchItemPayload } from 'providers/contexts/extensions/SavedSearches';
import { qsOptions } from 'providers/clients/httpClient';
import qs from 'qs';
import { SortOption } from './components/SortDropdown';
import { DASHBOARD_RESOURCE } from 'screens/dashboard/custom-dashboards/utils/constants';
import { WireSearchContextType } from 'screens/syndication/views/search/syndication/WireSearchWithContext';

// Extend dayjs with UTC, timezone, and custom parse format plugins.
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

type BuildSavedSearchParamsProps = {
  payload?: SaveSearchItemPayload;
  addParams?: AnyRecord;
  timeZone?: string;
};

// Normalize legacy 'deleted' values from BLOX admin ('true'/'false' or boolean
// true/false) to NXT values ('1'/'0'). Pass-through for already-NXT values.
export function normalizeLegacyDeletedValue(value: unknown): unknown {
  if (value === 'false' || value === false) return '0';
  if (value === 'true' || value === true) return '1';
  return value;
}

const searchDateFormats = ['MMM D, YYYY', 'MMMM D, YYYY', 'YYYY-MM-DD'];

export const subscriptionDateRangeKeys = new Set([
  'subscription_start_time[min]',
  'subscription_start_time[max]',
  'subscription_expire_time[min]',
  'subscription_expire_time[max]',
]);

// Normalize date range values for search params, ensuring they are in ISO format and adjusted to the correct timezone
export function normalizeSearchDateRangeValue(
  key: string,
  value: string,
  timeZone = 'UTC',
): string {
  if (!subscriptionDateRangeKeys.has(key)) {
    return value;
  }

  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return value;
  }

  const parsedValue = dayjs(trimmedValue, searchDateFormats, true);
  if (!parsedValue.isValid()) {
    return value;
  }

  const normalizedValue = parsedValue.tz(timeZone, true);
  if (key.endsWith('[min]')) {
    return normalizedValue.startOf('day').toISOString();
  }

  return normalizedValue.endOf('day').toISOString();
}

// Build query paramater string from saved search payload for editorial
export function buildSavedSearchParams({
  payload,
  addParams,
  timeZone = 'UTC',
}: BuildSavedSearchParamsProps): string {
  if (!payload || Object.keys(payload).length === 0) {
    return '';
  }

  const extraParams = addParams ?? {};

  // Specific key mappings / value mappings as we may
  // need to override from the saved search payload
  const qpKeyMap: AnyRecord = {
    // Editorial Specific Key Mappings
    start_time_from: 'start_time[min]',
    start_time_to: 'start_time[max]',
    author_uuid: 'author',
    print_slug_slug: 'print_slug',
    'category[]': 'category',
    'type[]': 'type',
    'subject[]': 'subject',
  };

  const qpValueMap: AnyRecord = {
    // Editorial Specific Value Mappings
    advertorial:
      payload.advertorial === 'exclude' || payload.advertorial === 'no'
        ? 'no'
        : payload.advertorial === 'include' || payload.advertorial === 'yes'
          ? 'yes'
          : undefined,
    contributed:
      payload.contributed === false || payload.contributed === 'no'
        ? 'no'
        : payload.contributed === true || payload.contributed === 'yes'
          ? 'yes'
          : undefined,
    deleted: normalizeLegacyDeletedValue(payload.deleted),
  };

  // filter out null/undefined, empty arr/obj, and add in extraParams
  // to the the payload. Saved Searches also (quick filters) do not
  // seem to include sort/dir, as most channels do not support sorting
  // so we're not going to include them here.
  const sanitizedPayload = removeAllEmptyNullUndefinedValues({
    ...payload,
    ...extraParams,
  });

  // Accumulate into the final object to be stringified into query params,
  // as certain keys need to be mapped as different query param names
  const finalPayload = Object.entries(sanitizedPayload).reduce((acc, [key, value]) => {
    const mappedKey = qpKeyMap[key] ?? key;
    const mappedValue = key in qpValueMap ? qpValueMap[key] : value;
    const finalValue =
      typeof mappedValue === 'string'
        ? normalizeSearchDateRangeValue(mappedKey, mappedValue, timeZone)
        : mappedValue;
    if (finalValue !== undefined && finalValue !== '') {
      acc[mappedKey] = Array.isArray(finalValue) ? finalValue.join(',') : finalValue;
    }
    return acc;
  }, {} as AnyRecord);

  // Convert final object to query string
  // return qps if at least one key exists in payload
  const hasKeys = Boolean(Object.keys(finalPayload).length > 0);
  const qpStr = `?${qs.stringify(finalPayload, qsOptions)}`;
  const result = hasKeys ? qpStr : '';

  return result;
}

/**
 * Get app param zod validator and qp parser by RA resource.
 * In most resources, 'app' is a string[] array, but
 * in some edge cases the param is a string (single value).
 *
 * TODO: Check https://townnews.atlassian.net/browse/CMSADMIN-1462?focusedCommentId=359639
 * to see if this will be adjusted for dashboards.
 *
 */

type GetAppParamDetailsArgs = {
  resource?: string;
};

type GetAppParamDetailsResult = {
  validator: z.ZodOptional<z.ZodString> | z.ZodOptional<z.ZodArray<z.ZodString>>;
  parser: (queryParams: URLSearchParams) => string | string[] | undefined;
};

const getAppParamDetails = ({
  resource,
}: GetAppParamDetailsArgs): GetAppParamDetailsResult => {
  switch (resource) {
    case 'pay/transactions':
    case DASHBOARD_RESOURCE: {
      return {
        validator: z.string().optional(),
        parser: (queryParams: URLSearchParams) => queryParams.get('app') || undefined,
      };
    }
    default: {
      return {
        validator: z.array(z.string()).optional(),
        parser: (queryParams: URLSearchParams) =>
          queryParams.get('app')?.split(',') || undefined,
      };
    }
  }
};

/**
 * Get type param zod validator and qp parser by RA resource.
 * In most resources, 'type' is a string[] array, but
 * in some edge cases the param is a string (single value).
 */
type GetTypeParamDetailsArgs = {
  resource?: string;
};

type GetTypeParamDetailsResult = {
  validator: z.ZodOptional<z.ZodString> | z.ZodOptional<z.ZodArray<z.ZodString>>;
  parser: (queryParams: URLSearchParams) => string | string[] | undefined;
};

const getTypeParamDetails = ({
  resource,
}: GetTypeParamDetailsArgs): GetTypeParamDetailsResult => {
  switch (resource) {
    case DASHBOARD_RESOURCE: {
      return {
        validator: z.string().optional(),
        parser: (queryParams: URLSearchParams) => queryParams.get('type') || undefined,
      };
    }
    default: {
      return {
        validator: z.array(z.string()).optional(),
        parser: (queryParams: URLSearchParams) =>
          queryParams.get('type')?.split(',') || undefined,
      };
    }
  }
};

type SearchZodValidatorFnParams = {
  // Resource from RA context
  resource?: string;
  // Valid sort keys for the resource
  validSortKeys?: CreateZodValidatorFnParams['validSortKeys'];
  // User is viewing 'normal' user search with subscription enabled on site.
  withSub?: boolean;
};

// Zod Validation Schema for Search Params
export function createSearchZodValidator({
  validSortKeys = [],
  withSub,
  resource,
}: SearchZodValidatorFnParams) {
  // App is array in most cases, on others it can be a string
  const { validator: appValidator } = getAppParamDetails({ resource: resource });
  const { validator: typeValidator } = getTypeParamDetails({ resource: resource });

  const validator = z.object({
    // Special cases
    app: appValidator,
    type: typeValidator,

    // Universal Search - used in multiple search types
    query: z.string().optional(),
    semantic: z.string().optional(),
    semantic_nearest: z.coerce.number().int().optional(),
    sort: z
      .string()
      .refine((field) => !field || validSortKeys.includes(field))
      .optional(),
    dir: z
      .string()
      .refine((dir) => !dir || ['ASC', 'DESC'].includes(dir))
      .optional(),
    id: z.string().optional(),

    // Editorial Search Specific
    author: z.string().optional(),
    contributed: z.string().optional(),
    advertorial: z.string().optional(),
    section: z.array(z.string()).optional(),
    template: z.string().optional(),
    uuid: z.array(z.string()).optional(),
    print_slug: z.string().optional(),
    publish_status: z.array(z.string()).optional(),
    'start_time[min]': z.string().optional(),
    'start_time[max]': z.string().optional(),
    deleted: z.string().optional(),
    flag: z.array(z.string()).optional(),
    print_ready: z.string().optional(),
    'print_depth[min]': z.string().optional(),
    'print_depth[max]': z.string().optional(),
    print_publication: z.string().optional(),
    'print_publication_date[min]': z.string().optional(),
    'print_publication_date[max]': z.string().optional(),
    print_publication_status: z.string().optional(),
    photo_sales: z.string().optional(),
    draft: z.string().optional(),
    workflow: z.array(z.string()).optional(),
    workflow_process: z.array(z.string()).optional(),

    // User Search Specific
    account_type: z
      .string()
      .refine((data) => data === 'normal' || data === 'admin')
      .optional(),
    account_status: z
      .string()
      .refine((data) => userStatusChoices.some((choice) => choice.value === data))
      .optional(),
    auth_type: z
      .string()
      .refine((data) => authTypeChoices.some((choice) => choice.value === data))
      .optional(),
    banned: z
      .string()
      .refine((data) => bannedChoices.some((choice) => choice.value === data))
      .optional(),
    subscription: z.boolean().optional(),
    view_by: z
      .string()
      .refine((data) => {
        const check = withSub
          ? userSubViewByChoices.some((choice) => choice.value === data)
          : false;
        return check;
      })
      .optional(),
    create_time: z.string().optional(),
    subscription_state: z
      .string()
      .refine((data) => {
        const check = withSub
          ? subscriptionStateChoices.some((choice) => choice.value === data)
          : false;
        return check;
      })
      .optional(),
    subscription_service_type: z.array(z.string()).optional(),

    // Not an actual API param, see VLF type comments.
    subRateNames: z.array(z.string()).optional(),

    subscription_rate_id: z
      .array(z.string())
      .refine((data) => withSub)
      .optional(),

    'subscription_start_time[min]': z
      .string()
      .refine((data) => withSub)
      .optional(),
    'subscription_start_time[max]': z
      .string()
      .refine((data) => withSub)
      .optional(),
    'subscription_rate_price_range[min]': z
      .string()
      .refine((data) => withSub)
      .optional(),
    'subscription_rate_price_range[max]': z
      .string()
      .refine((data) => withSub)
      .optional(),
    'subscription_expire_time[min]': z
      .string()
      .refine((data) => withSub)
      .optional(),
    'subscription_expire_time[max]': z
      .string()
      .refine((data) => withSub)
      .optional(),

    // Syndication - Channel Search Specific
    driver: z.array(z.string()).optional(),
    channel: z.string().optional(),
    raw_query: z.string().optional(), //This is for PR News channels that use 'raw_query' instead of 'query'
    slug: z.string().optional(),
    category: z.array(z.string()).optional(),
    subject: z.array(z.string()).optional(),
    industry: z.array(z.string()).optional(),
    language: z.array(z.string()).optional(),
    geography: z.array(z.string()).optional(),
    platform: z.string().optional(),
    include_nonpublishable: z.string().optional(),
    feed: z.string().optional(),
    feed_type: z.string().optional(),
    post_type: z.string().optional(),
    media_type: z.string().optional(),
    eventType: z.string().optional(),
    safeSearch: z.string().optional(),
    vms_media_type: z.string().optional(),
    reuters_types: z.array(z.string()).optional(),
    'date_range[min]': z.string().optional(),
    'date_range[max]': z.string().optional(),
    'mod_date_range[min]': z.string().optional(),
    'mod_date_range[max]': z.string().optional(),
    'order_range[min]': z.string().optional(),
    'order_range[max]': z.string().optional(),
    'start_range[min]': z.string().optional(),
    'start_range[max]': z.string().optional(),
    'end_range[min]': z.string().optional(),
    'end_range[max]': z.string().optional(),
    date: z.string().optional(),

    // Subscription / Transactions
    product: z.string().optional(),
    service_name: z.string().optional(),
    form_name: z.string().optional(),
    user_uuid: z.string().optional(),
    external_id: z.string().optional(),
    state: z.string().optional(),
    test_status: z.string().optional(),
    payment_handler: z.string().optional(),
    payment_type: z.string().optional(),
    subscription_service: z.array(z.string()).optional(),
    cit_mit: z.string().optional(),
    initiated_by: z.string().optional(),
    lifecycle: z.string().optional(),
    'transaction_date_range[min]': z.string().optional(),
    'transaction_date_range[max]': z.string().optional(),

    // Services
    is_active: z.string().optional(),
    is_testmode: z.string().optional(),
    is_hidden: z.string().optional(),
    is_deleted: z.string().optional(),
    has_rates: z.string().optional(),

    // EmailReach specific QP
    account_id: z.string().optional(),
    lists: z.string().optional(),
    filter: z.string().optional(),
    list_type: z.string().optional(),
    campaign_type: z.string().optional(),
    ids: z.string().optional(),
    status: z.string().optional(),

    // Notifier (broadcast/message)
    send_status: z.array(z.string()).optional(),
    channels: z.array(z.string()).optional(),
    'send_date[min]': z.string().optional(),
    'send_date[max]': z.string().optional(),

    // Notifier (broadcast/channel)
    disabled: z
      .string()
      .refine((val) => val === 'true' || val === 'false')
      .optional(),
    message_id: z.string().optional(),
  });

  return validator;
}

type GetKnownSearchQueryParamsArgs = {
  queryParams: URLSearchParams;
  resource?: string;
};

export function getKnownSearchQueryParams({
  queryParams,
  resource,
}: GetKnownSearchQueryParamsArgs): ValidListFilterType {
  const { parser: appParser } = getAppParamDetails({ resource: resource });
  const { parser: typeParser } = getTypeParamDetails({ resource: resource });
  const appValue = appParser(queryParams);
  const typeValue = typeParser(queryParams);

  const semanticNearestValue = queryParams.get('semantic_nearest');
  const semanticNearestNumber =
    semanticNearestValue !== null ? Number(semanticNearestValue) : undefined;
  const semanticNearest =
    semanticNearestNumber !== undefined && Number.isInteger(semanticNearestNumber)
      ? semanticNearestNumber
      : undefined;

  /**
   * Note: subRateNames is not an actual API param, see VLF type comments.
   * The getAll() method returns an array, empty if no keys found.
   */
  const subRateNamesQp = queryParams.getAll('subRateNames');
  const subRateNamesValue = Boolean(subRateNamesQp.length) ? subRateNamesQp : undefined;

  const definedQP = {
    // Special cases
    app: appValue,

    // Universal Search - used in multiple search types
    query: queryParams.get('query') || undefined,
    semantic: queryParams.get('semantic') || undefined,
    semantic_nearest: semanticNearest,
    sort: queryParams.get('sort') || undefined,
    dir: queryParams.get('dir') || undefined,
    flag: queryParams.get('flag')?.split(',') || undefined,
    id: queryParams.get('id') || undefined,

    // Editorial Search Specific
    'start_time[max]': queryParams.get('start_time[max]') || undefined,
    'start_time[min]': queryParams.get('start_time[min]') || undefined,
    advertorial: queryParams.get('advertorial') || undefined,
    author: queryParams.get('author') || undefined,
    contributed: queryParams.get('contributed') || undefined,
    deleted:
      (normalizeLegacyDeletedValue(queryParams.get('deleted')) as string | undefined) ||
      undefined,
    draft: queryParams.get('draft') || undefined,
    photo_sales: queryParams.get('photo_sales') || undefined,
    print_ready: queryParams.get('print_ready') || undefined,
    'print_depth[min]': queryParams.get('print_depth[min]') || undefined,
    'print_depth[max]': queryParams.get('print_depth[max]') || undefined,
    print_publication: queryParams.get('print_publication') || undefined,
    'print_publication_date[min]':
      queryParams.get('print_publication_date[min]') || undefined,
    'print_publication_date[max]':
      queryParams.get('print_publication_date[max]') || undefined,
    print_publication_status: queryParams.get('print_publication_status') || undefined,
    print_slug: queryParams.get('print_slug') || undefined,
    publish_status: queryParams.get('publish_status')?.split(',') || undefined,
    section: queryParams.get('section')?.split(',') || undefined,
    type: typeValue,
    template: queryParams.get('template') || undefined,
    workflow_process: queryParams.get('workflow_process')?.split(',') || undefined,
    workflow: queryParams.get('workflow')?.split(',') || undefined,

    // User Search Specific
    account_type: queryParams.get('account_type') || undefined,
    account_status: queryParams.get('account_status') || undefined,
    auth_type: queryParams.get('auth_type') || undefined,
    banned: queryParams.get('banned') || undefined,
    subscription: queryParams.get('subscription') || undefined,
    view_by: queryParams.get('view_by') || undefined,
    create_time: queryParams.get('create_time') || undefined,
    subscription_state: queryParams.get('subscription_state') || undefined,
    subscription_service_type:
      queryParams.get('subscription_service_type')?.split(',') || undefined,

    subRateNames: subRateNamesValue,
    subscription_rate_id:
      queryParams.get('subscription_rate_id')?.split(',') || undefined,

    'subscription_start_time[min]':
      queryParams.get('subscription_start_time[min]') || undefined,
    'subscription_start_time[max]':
      queryParams.get('subscription_start_time[max]') || undefined,
    'subscription_rate_price_range[min]':
      queryParams.get('subscription_rate_price_range[min]') || undefined,
    'subscription_rate_price_range[max]':
      queryParams.get('subscription_rate_price_range[max]') || undefined,
    'subscription_expire_time[min]':
      queryParams.get('subscription_expire_time[min]') || undefined,
    'subscription_expire_time[max]':
      queryParams.get('subscription_expire_time[max]') || undefined,

    // Syndication - Channel Search Specific
    driver: queryParams.get('driver')?.split(',') || undefined,
    channel: queryParams.get('channel_id') || undefined,
    // PR News channels use 'raw_query' instead of 'query'
    raw_query: queryParams.get('raw_query') || undefined,
    slug: queryParams.get('slug') || undefined,
    category: queryParams.get('category')?.split(',') || undefined,
    subject: queryParams.get('subject')?.split(',') || undefined,
    industry: queryParams.get('industry')?.split(',') || undefined,
    language: queryParams.get('language')?.split(',') || undefined,
    geography: queryParams.get('geography')?.split(',') || undefined,
    platform: queryParams.get('platform') || undefined,
    include_nonpublishable: queryParams.get('include_nonpublishable') || undefined,
    feed: queryParams.get('feed') || undefined,
    feed_type: queryParams.get('feed_type') || undefined,
    post_type: queryParams.get('post_type') || undefined,
    media_type: queryParams.get('media_type') || undefined,
    eventType: queryParams.get('eventType') || undefined,
    safeSearch: queryParams.get('safeSearch') || undefined,
    vms_media_type: queryParams.get('vms_media_type') || undefined,
    reuters_types: queryParams.get('reuters_types')?.split(',') || undefined,
    'date_range[min]': queryParams.get('date_range[min]') || undefined,
    'date_range[max]': queryParams.get('date_range[max]') || undefined,
    'mod_date_range[min]': queryParams.get('mod_date_range[min]') || undefined,
    'mod_date_range[max]': queryParams.get('mod_date_range[max]') || undefined,
    'order_range[min]': queryParams.get('order_range[min]') || undefined,
    'order_range[max]': queryParams.get('order_range[max]') || undefined,
    'start_range[min]': queryParams.get('start_range[min]') || undefined,
    'start_range[max]': queryParams.get('start_range[max]') || undefined,
    'end_range[min]': queryParams.get('end_range[min]') || undefined,
    'end_range[max]': queryParams.get('end_range[max]') || undefined,
    date: queryParams.get('date') || undefined,

    // Subscription / Transactions
    subscription_service:
      queryParams.get('subscription_service')?.split(',') || undefined,
    product: queryParams.get('product') || undefined,
    service_name: queryParams.get('service_name') || undefined,
    form_name: queryParams.get('form_name') || undefined,
    user_uuid: queryParams.get('user_uuid') || undefined,
    external_id: queryParams.get('external_id') || undefined,
    state: queryParams.get('state') || undefined,
    test_status: queryParams.get('test_status') || undefined,
    payment_handler: queryParams.get('payment_handler') || undefined,
    payment_type: queryParams.get('payment_type') || undefined,
    cit_mit: queryParams.get('cit_mit') || undefined,
    initiated_by: queryParams.get('initiated_by') || undefined,
    lifecycle: queryParams.get('lifecycle') || undefined,
    'transaction_date_range[min]':
      queryParams.get('transaction_date_range[min]') || undefined,
    'transaction_date_range[max]':
      queryParams.get('transaction_date_range[max]') || undefined,

    // Services
    is_active: queryParams.get('is_active') || undefined,
    is_testmode: queryParams.get('is_testmode') || undefined,
    is_hidden: queryParams.get('is_hidden') || undefined,
    is_deleted: queryParams.get('is_deleted') || undefined,
    has_rates: queryParams.get('has_rates') || undefined,

    // EmailReach specific QP
    account_id: queryParams.get('account_id') || undefined,
    lists: queryParams.get('lists') || undefined,
    filter: queryParams.get('filter') || undefined,
    list_type: queryParams.get('list_type') || undefined,
    campaign_type: queryParams.get('campaign_type') || undefined,
    ids: queryParams.get('ids') || undefined,
    status: queryParams.get('status') || undefined,

    // Notifier (broadcast/message)
    send_status: queryParams.get('send_status')?.split(',') || undefined,
    channels: queryParams.get('channels')?.split(',') || undefined,
    'send_date[min]': queryParams.get('send_date[min]') || undefined,
    'send_date[max]': queryParams.get('send_date[max]') || undefined,

    // Notifier (broadcast/channel)
    disabled: queryParams.get('disabled') || undefined,
    message_id: queryParams.get('message_id') || undefined,
  };

  return definedQP;
}

/**
 * Reserved for specific edge cases.
 * Keys that require repeat qp format instead of comma join/parse.
 * eg: `/?key=value1&key=value2` instead of `/?key=value1,value2`
 *
 * This is rare in nxt, only for edge cases where the url qp
 * values may contain commas or other delimiter characters which
 * can interfere with the default parse/split in getKnownSearchQueryParams,
 * and if no other delimiter could be used instead of a comma there.
 * eg: `qp.get('key').split(',')` vs `qp.getAll('key')` for repeat qp keys.
 * https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams/getAll
 * Note: the search filter objects (eg: staged, validated filters)
 * should remain the same, under one key eg: `key:[v1,v2]`
 * as this only affects conversion to/from url query string.
 *
 * NOTE: Repeat qp's are generally multiselect type, but
 * most multi-selects do NOT require repeat qp format and
 * can continue using the default comma split setup.
 *
 */

type TGetRepeatQpConfigArgs = { resource?: string };
type TGetRepeatQpConfigResult = { keys: string[] };

export function getRepeatQpConfig({
  resource,
}: TGetRepeatQpConfigArgs): TGetRepeatQpConfigResult {
  const empty = { keys: [] };
  if (!resource) {
    return empty;
  }
  switch (resource) {
    case 'user': {
      const result = { keys: ['subRateNames'] };
      return result;
    }
    default: {
      return empty;
    }
  }
}

export function getViewKeyLS(resource?: string): SearchContextType['view'] {
  switch (resource) {
    //Resources that supports multiple views
    case 'editorial':
    case 'wire/channel/search': {
      const savedView = localStorage.getItem('currentView');
      if (savedView === 'list' || savedView === 'card' || savedView === 'summary') {
        return savedView;
      }
      return 'list';
    }
    case 'broadcast/message': {
      return 'list';
    }
    default: {
      return 'list';
    }
  }
}

export function getPreferenceKey(
  view: SearchContextType['view'],
): SearchContextType['preferenceKey'] {
  return view === 'summary' ? 'search-summary-view' : 'search-table-view';
}

export const formatFieldToLabel = (field: string): string => {
  // Convert snake_case or camelCase to Title Case with spaces
  return (
    field
      // Insert space before capital letters for camelCase
      .replace(/([A-Z])/g, ' $1')
      // Replace underscores with spaces for snake_case
      .replace(/_/g, ' ')
      // Trim any extra spaces and capitalize first letter of each word
      .trim()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  );
};

export type TResourceContexts = {
  'wire/channel/search': WireSearchContextType | undefined;
  [key: string]: any;
};

type GetSortOptionsArgs = {
  resource?: string;
  resourceContexts?: TResourceContexts;
};

// Helper function for determining sort options based on the resource context
export const getSortOptions = ({
  resource,
  resourceContexts,
}: GetSortOptionsArgs): SortOption[] => {
  switch (resource) {
    case 'editorial':
    case 'editorial/templates': {
      return editorialSortOptions;
    }
    case 'wire/channel': {
      return channelsSortOptions;
    }
    case 'wire/channel/search': {
      const wireContext = resourceContexts?.['wire/channel/search'];
      const sortOptionsItem = wireContext?.channelOptions?.find(
        (option: any) => option.type === 'sort',
      );
      return Array.isArray(sortOptionsItem?.options)
        ? sortOptionsItem.options.map((option: any) => {
            // Format the label since it's the same as field
            const formattedLabel = formatFieldToLabel(option.field);
            return {
              field: option.field,
              label: formattedLabel,
            };
          })
        : [];
    }
    case 'user/account/multisite':
    case 'user': {
      return userSortOptions;
    }
    case 'subscription/services': {
      return servicesSortOptions;
    }
    case 'broadcast/message': {
      return broadcastMessageSortOptions;
    }
    case 'broadcast/channel': {
      return broadcastChannelSortOptions;
    }
    case 'pay/transactions': {
      return transactionsSortOptions;
    }
    case DASHBOARD_RESOURCE: {
      return dashboardSortOptions;
    }
    default: {
      return [];
    }
  }
};

// Helper function for determining sort options based on the resource context
export const getSortableOptions = (
  resource?: string,
  resourceContexts?: TResourceContexts,
) => {
  switch (resource) {
    case 'editorial':
    case 'editorial/templates': {
      // Use editorial dropdown options
      const sortOptions = editorialSortOptions.map((option: any) => option.field);
      return sortOptions;
    }
    case 'wire/channel': {
      // Use channels dropdown options
      const sortOptions = channelsSortOptions.map((option: any) => option.field);
      return sortOptions;
    }
    case 'wire/channel/search': {
      const wireContext = resourceContexts?.['wire/channel/search'];
      const sortOptionsItem = wireContext?.channelOptions?.find(
        (option: any) => option.type === 'sort',
      );
      return Array.isArray(sortOptionsItem?.options)
        ? sortOptionsItem.options.map((option: any) => option.field)
        : [];
    }
    case 'user/account/multisite':
    case 'user': {
      return userSortOptions.map((option: any) => option.field);
    }
    case 'subscription/services': {
      return servicesSortOptions.map((option: any) => option.field);
    }
    case 'broadcast/message': {
      return broadcastMessageSortOptions.map((option: any) => option.field);
    }
    case 'broadcast/channel': {
      return broadcastChannelSortOptions.map((option: any) => option.field);
    }
    case 'pay/transactions': {
      return transactionsSortOptions.map((option: any) => option.field);
    }
    case DASHBOARD_RESOURCE: {
      return dashboardSortOptions.map((option: any) => option.field);
    }
    default: {
      return [];
    }
  }
};

type GetPlaceholderTextArgs = {
  resource?: string;
  resourceContexts: TResourceContexts;
  isNormal: boolean;
  t: TFunction;
};

// Helper function for getting the placeholder text based on the resource type.
export const getPlaceholderText = ({
  resource,
  resourceContexts,
  isNormal,
  t,
}: GetPlaceholderTextArgs) => {
  switch (resource) {
    case 'editorial': {
      return t('blox.search.placeholder', 'Search content...');
    }
    case 'editorial/templates': {
      return t('blox.search.table.search_templates', 'Search templates...');
    }
    case 'user/account/multisite': {
      return t('blox.user.search.action');
    }
    case 'user': {
      return isNormal ? t('blox.user.search.action') : t('blox.user.search.admin.action');
    }
    case 'wire/channel/search': {
      const wireContext = resourceContexts?.['wire/channel/search'];
      const channelName = wireContext?.channels?.find(
        (channel: any) => channel.value === wireContext?.selectedChannel,
      )?.label;
      return `Search ${channelName || ''}...`;
    }
    case 'emailreach/contact': {
      return t('blox.email_reach.search.placeholder');
    }
    case 'emailreach/messages': {
      return t('blox.email_reach.messages.search.label');
    }
    case 'broadcast/message': {
      return t('blox.notifier.search.search_messages');
    }
    case 'pay/transactions': {
      return t('blox.transactions.search.placeholder', 'Search transactions...');
    }
    default: {
      const strSearch = t('blox.general.search');
      const strPlaceholderTxt = `${strSearch}...`;
      return strPlaceholderTxt;
    }
  }
};

/**
 * Check if a string contains an odd number of double quotes (i.e. unmatched quotes).
 */
export function hasUnmatchedQuotes(value: string): boolean {
  const matches = value.match(/"/g);
  return (matches?.length || 0) % 2 !== 0;
}

/**
 * Check if any string value in a filter object contains unmatched quotes.
 * Useful for preventing auto-search when the user is still crafting a quoted query.
 */
export function filterHasUnmatchedQuotes(filter: Record<string, any>): boolean {
  return Object.values(filter).some(
    (value) => typeof value === 'string' && hasUnmatchedQuotes(value),
  );
}

type CheckSearchEnabledArgs = {
  filter?: Record<string, any>;
  resource?: string;
};

type CheckSearchEnabledResult = { isEnabled: boolean };

/**
 * Centralized check to determine if a search query should run.
 * Bundles all validation logic (e.g. unmatched quotes, resource-specific requirements)
 * so callers don't need to duplicate these checks.
 */
export function checkSearchEnabled({
  filter,
  resource,
}: CheckSearchEnabledArgs): CheckSearchEnabledResult {
  if (filter && filterHasUnmatchedQuotes(filter)) {
    return { isEnabled: false };
  }

  switch (resource) {
    case 'wire/channel/search': {
      if (!filter?.channel) {
        return { isEnabled: false };
      }
      break;
    }
  }

  return { isEnabled: true };
}
